import { useState, type FormEvent } from "react";
import type { ViewerTokenCreated, ViewerTokenStatus } from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { useResource } from "../api/useResource";
import { formatAgo, formatWhen } from "../lib/format";

const TOKEN_FILE = "~/.config/auto-pools/viewer-token";
// umask before the redirect and a fresh file (an old one keeps its mode): never readable by others.
const SAVE_COMMAND =
  "(umask 077 && mkdir -p ~/.config/auto-pools && chmod 700 ~/.config/auto-pools && " +
  `rm -f ${TOKEN_FILE} && pbpaste > ${TOKEN_FILE})`;

/** A refused password keeps the dialog open for another try, as the Bybit key dialog does. */
function createErrorText(reason: unknown): string {
  if (reason instanceof ApiError && reason.code === "AUTH_FAILED")
    return "The password was not accepted. Try again.";
  return reason instanceof ApiError ? reason.message : "Could not create a viewer token";
}

/** A clipboard write that never throws into the render path (private window, denied permission). */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Settings card for the one long-lived viewer token (read-only access for the local screenshot
 * tool). The plaintext token exists only in the create response: it lives in this component's
 * state while the dialog shows it and is dropped when the dialog closes — never logged, put in a
 * URL, or written to browser storage.
 */
export function ViewerTokenCard() {
  const status = useResource<ViewerTokenStatus>("viewer-token", (signal) =>
    api("/auth/viewer-token", { signal, sensitive: true }),
  );
  const [dialog, setDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const active = status.data?.active === true;

  function closeDialog() {
    if (pending) return;
    setDialog(false);
    setPassword("");
    setToken(null);
    setError(null);
    setCopied(false);
  }

  async function submitCreate(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const created = await api<ViewerTokenCreated>("/auth/viewer-token", {
        method: "POST",
        body: { password },
        sensitive: true,
        passwordReauthentication: true,
      });
      setPassword("");
      setToken(created.token);
      status.reload();
    } catch (reason) {
      setError(createErrorText(reason));
    } finally {
      setPending(false);
    }
  }

  async function revoke() {
    if (pending) return;
    setPending(true);
    setRevokeError(null);
    try {
      await api("/auth/viewer-token/revoke", { method: "POST", sensitive: true });
      status.reload();
    } catch (reason) {
      setRevokeError(reason instanceof ApiError ? reason.message : "Could not revoke the token");
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel settings-card viewer-token-card">
      <h2>Read-only access</h2>
      <p className="muted viewer-token-intro">
        A viewer token opens this panel for reading only: positions, charts, the autopilot and the
        pool catalog. Every action is refused by the server. It stays valid until revoked or the
        password changes.
      </p>
      {status.loading && !status.data && <p className="muted">Checking the token…</p>}
      {status.error && <p className="inline-alert error">{status.error.message}</p>}
      {status.data && (
        <>
          <div className={`spot-key-banner ${active ? "good" : ""}`}>
            <i className={`status-dot ${active ? "good" : ""}`} />
            <div>
              <b>{active ? "Viewer token active" : "No viewer token"}</b>
              <p>
                {active
                  ? "A local tool holding the token can open a read-only session."
                  : "Nothing can open a read-only session."}
              </p>
            </div>
          </div>
          {active && (
            <div className="spot-key-detail-rows">
              <div className="spot-key-detail-row">
                <span>Active since</span>
                <span className="num">
                  {status.data.createdAt ? formatWhen(status.data.createdAt) : "—"}
                </span>
              </div>
              <div className="spot-key-detail-row">
                <span>Last used</span>
                <span className="num">
                  {status.data.lastUsedAt ? formatAgo(status.data.lastUsedAt) : "never"}
                </span>
              </div>
            </div>
          )}
          <div className="spot-key-actions">
            <button
              type="button"
              className="button ghost"
              disabled={pending}
              onClick={() => {
                setRevokeError(null);
                setDialog(true);
              }}
            >
              {active ? "Create a new token" : "Create token"}
            </button>
            {active && (
              <button
                type="button"
                className="button danger"
                disabled={pending}
                onClick={() => void revoke()}
              >
                {pending && !dialog ? "Revoking…" : "Revoke"}
              </button>
            )}
          </div>
          {revokeError && (
            <p className="inline-alert error" role="alert">
              {revokeError}
            </p>
          )}
        </>
      )}

      {dialog && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            // The token is shown once: a stray click outside must not discard it unseen.
            if (event.target === event.currentTarget && token === null) closeDialog();
          }}
        >
          <section
            className="modal action-modal spot-key-dialog viewer-token-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="viewer-token-dialog-title"
          >
            <header className="modal-header">
              <h2 id="viewer-token-dialog-title">
                {token === null ? "Create a viewer token" : "Viewer token created"}
              </h2>
              <button
                className="icon-button"
                disabled={pending}
                onClick={closeDialog}
                aria-label={token === null ? "Cancel" : "Close"}
              >
                ×
              </button>
            </header>
            {token === null ? (
              <form className="stack" onSubmit={(event) => void submitCreate(event)}>
                {active && (
                  <p className="inline-alert warning">
                    The current token stops working as soon as the new one is created, and every
                    open read-only session ends.
                  </p>
                )}
                <label>
                  Account password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    required
                  />
                </label>
                <button className="button primary" disabled={!password || pending}>
                  {pending ? "Creating…" : "Create token"}
                </button>
                {error && (
                  <p className="inline-alert error" role="alert">
                    {error}
                  </p>
                )}
              </form>
            ) : (
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  closeDialog();
                }}
              >
                <p className="inline-alert warning">
                  Shown only once. It cannot be displayed again; create a new one if it is lost.
                </p>
                <div className="spot-key-public">
                  <code className="num spot-key-public-value" aria-label="Viewer token">
                    {token}
                  </code>
                  <button
                    type="button"
                    className="button ghost small"
                    onClick={() => {
                      void copy(token).then(setCopied);
                    }}
                  >
                    {copied ? "Copied" : "Copy token"}
                  </button>
                </div>
                <p className="viewer-token-save">
                  Copy it, then save it to <code className="num">{TOKEN_FILE}</code> on the computer
                  that runs the viewer tool, private from the first byte:{" "}
                  <code className="num">{SAVE_COMMAND}</code>
                </p>
                <button className="button primary">Done</button>
              </form>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
